import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Loader2, Handshake, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PARTY_COLOR = { D: "#3b82f6", R: "#ef4444", I: "#a855f7" };
const LABEL_W = 128;
const CELL = 11;

export const HeatmapView = ({ congress, chamber, onPair, exportRef, sidePanel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);
  const canvasRef = useRef();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/network/heatmap`, { params: { congress, chamber: chamber || "Senate", limit: 60 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [congress, chamber]);

  const cellColor = (v) => {
    if (v == null) return "#12161f";
    if (v >= 50) {
      const t = (v - 50) / 50;
      return `rgba(249,115,22,${0.10 + t * t * 0.9})`; // orange = agreement
    }
    const t = (50 - v) / 50;
    return `rgba(59,130,246,${0.08 + t * 0.65})`; // blue = disagreement
  };

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv || !data) return;
    const n = data.members.length;
    const size = LABEL_W + n * CELL;
    cv.width = size; cv.height = size;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#0b0e14";
    ctx.fillRect(0, 0, size, size);
    // row name labels + party dots
    ctx.font = "9px Inter, sans-serif";
    ctx.textBaseline = "middle";
    data.members.forEach((m, i) => {
      const y = LABEL_W + i * CELL + CELL / 2;
      ctx.fillStyle = PARTY_COLOR[m.party] || "#9ca3af";
      ctx.beginPath(); ctx.arc(6, y, 2.5, 0, 2 * Math.PI); ctx.fill();
      ctx.fillStyle = hover && (hover.i === i || hover.j === i) ? "#f97316" : "rgba(255,255,255,0.62)";
      ctx.textAlign = "left";
      ctx.fillText(m.name.split(",")[0].slice(0, 16), 13, y);
    });
    // column labels (rotated)
    data.members.forEach((m, j) => {
      const x = LABEL_W + j * CELL + CELL / 2;
      ctx.save();
      ctx.translate(x, LABEL_W - 6);
      ctx.rotate(-Math.PI / 2.6);
      ctx.fillStyle = hover && (hover.i === j || hover.j === j) ? "#f97316" : "rgba(255,255,255,0.45)";
      ctx.textAlign = "left";
      ctx.fillText(m.name.split(",")[0].slice(0, 14), 0, 0);
      ctx.restore();
    });
    for (let i = 0; i < data.members.length; i++) {
      for (let j = 0; j < data.members.length; j++) {
        ctx.fillStyle = cellColor(data.matrix[i][j]);
        ctx.fillRect(LABEL_W + j * CELL, LABEL_W + i * CELL, CELL - 1, CELL - 1);
      }
    }
    if (hover) {
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 1;
      ctx.strokeRect(LABEL_W + hover.j * CELL - 0.5, LABEL_W + hover.i * CELL - 0.5, CELL, CELL);
    }
    if (exportRef) exportRef.current = cv;
  }, [data, hover, exportRef]);

  useEffect(() => { draw(); }, [draw]);

  const locate = (e) => {
    const cv = canvasRef.current;
    if (!cv || !data) return null;
    const rect = cv.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (cv.width / rect.width) - LABEL_W;
    const y = (e.clientY - rect.top) * (cv.height / rect.height) - LABEL_W;
    const j = Math.floor(x / CELL), i = Math.floor(y / CELL);
    const n = data.members.length;
    if (i < 0 || j < 0 || i >= n || j >= n || i === j) return null;
    return { i, j, a: data.members[i], b: data.members[j], v: data.matrix[i][j] };
  };

  const PairRow = ({ p, tone }) => (
    <button onClick={() => onPair(p.aId, p.bId)}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 text-left"
      data-testid={`pair-${p.aId}-${p.bId}`}>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PARTY_COLOR[p.aParty] }} />
      <span className="text-[12px] text-white/80 truncate">{p.a.split(",")[0]}</span>
      <span className="text-white/30 text-[11px]">×</span>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PARTY_COLOR[p.bParty] }} />
      <span className="text-[12px] text-white/80 truncate">{p.b.split(",")[0]}</span>
      <span className={`text-[11px] font-mono ml-auto shrink-0 ${tone}`}>{p.value}%</span>
    </button>
  );

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 lg:col-span-8">
        <div className="relative whip-surface rounded-xl border border-white/10 p-4" data-testid="heatmap-view">
          {loading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0b0e14]/70"><Loader2 className="w-6 h-6 text-orange-400 animate-spin" /></div>}
          <canvas
            ref={canvasRef}
            data-testid="heatmap-canvas"
            className="w-full h-auto cursor-crosshair rounded"
            onMouseMove={(e) => setHover(locate(e))}
            onMouseLeave={() => setHover(null)}
            onClick={(e) => { const c = locate(e); if (c && c.v != null) onPair(c.a.id, c.b.id); }}
          />
          {hover && (
            <div className="absolute bottom-6 left-6 bg-[#141924] border border-white/15 rounded-lg px-3 py-2 text-xs text-white/85 pointer-events-none z-10" data-testid="heatmap-tooltip">
              <b>{hover.a.name}</b> × <b>{hover.b.name}</b>
              <span className="text-orange-300 ml-2">{hover.v != null ? `${hover.v}% agreement` : "not enough shared votes"}</span>
              <span className="text-white/40 ml-2">click for detail</span>
            </div>
          )}
        </div>
      </div>
      <div className="col-span-12 lg:col-span-4 space-y-4">
        {sidePanel}
        <div className="whip-surface rounded-xl border border-white/10 p-4" data-testid="heatmap-cross-pairs">
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Handshake className="w-4 h-4 text-yellow-400" /> Strongest cross-party pairs</h3>
          <p className="text-[11px] text-white/40 mb-2">Members from opposite parties who vote together the most, the real aisle-crossers.</p>
          <div className="space-y-0.5">
            {(data?.highlights?.topCrossParty || []).map((p, i) => <PairRow key={i} p={p} tone="text-yellow-300" />)}
          </div>
        </div>
        <div className="whip-surface rounded-xl border border-white/10 p-4" data-testid="heatmap-friction-pairs">
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Flame className="w-4 h-4 text-rose-400" /> Same-party friction</h3>
          <p className="text-[11px] text-white/40 mb-2">Members of the same party who disagree the most, internal fault lines.</p>
          <div className="space-y-0.5">
            {(data?.highlights?.lowestSameParty || []).map((p, i) => <PairRow key={i} p={p} tone="text-rose-300" />)}
          </div>
        </div>
        <div className="whip-surface rounded-xl border border-white/10 p-4 text-[12px] text-white/50 leading-relaxed" data-testid="heatmap-hint">
          <span className="text-orange-300">Orange</span> = voting together, <span className="text-blue-300">blue</span> = voting apart. Members are ordered by party then ideology, so the two bright blocks are party unity, anything orange <i>between</i> the blocks is genuine cross-party alignment. Click any cell or pair for the shared-vote breakdown.
          {data?.meta && <span className="block mt-2 text-[11px] text-white/35">{data.meta.count} most-active members · {data.meta.chamber} · {data.meta.congress}th Congress</span>}
        </div>
      </div>
    </div>
  );
};

export default HeatmapView;
